import { useState } from "react";
import { withNamespaces } from "react-i18next";
import { Button } from "reactstrap";
import { getCollapsedItems, getCounterpartIcon } from "../helpers/flowCards";
import { getEndpointFieldLabels } from "../helpers/flowForm";

const Chip = ({ children }) => {
    return <span className="data-flow-v2__chip">{children}</span>;
};

const OverflowText = ({ items, labels, t }) => {
    const { visible, overflow } = getCollapsedItems(items);

    if (visible.length === 0) {
        return null;
    }

    return (
        <div className="data-flow-v2__text-row">
            {visible.map((item, index) => (
                <span key={item}>
                    {index > 0 ? ", " : ""}
                    {t(labels[item] || item)}
                </span>
            ))}
            {overflow > 0 && (
                <span className="data-flow-v2__overflow">{` +${overflow}`}</span>
            )}
        </div>
    );
};

const DetailBlock = ({ label, children }) => {
    if (!children) {
        return null;
    }

    return (
        <div>
            <div className="data-flow-v2__card-label">{label}</div>
            <div className="data-flow-v2__card-detail-value">{children}</div>
        </div>
    );
};

const DataFlowCard = ({
    card,
    methodLabels,
    typeLabels,
    categoryLabels,
    relationLabels,
    counterpartTypeLabel,
    isDeleting = false,
    onEdit,
    onDelete,
    t,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const counterpart = card.counterpart || {};
    const collapsedMethods = getCollapsedItems(card.methods);
    const identifier =
        counterpart.kind === "endpoint" ? counterpart.identifier : null;
    const identifierLabel = getEndpointFieldLabels(counterpart.type).identifier;
    const relationLabel = card.relation
        ? t(relationLabels[card.relation] || card.relation)
        : null;
    const hasDetails =
        !!card.purpose ||
        card.dataTypes.length > 0 ||
        card.dataCategories.length > 0 ||
        !!identifier;
    const visibleMethods = isExpanded ? card.methods : collapsedMethods.visible;
    const methodOverflow = isExpanded ? 0 : collapsedMethods.overflow;

    return (
        <div
            className={`data-flow-v2__card${
                isExpanded ? " data-flow-v2__card--expanded" : ""
            }`}
        >
            <div className="data-flow-v2__card-top">
                <div className="data-flow-v2__card-identity">
                    <i
                        className={`${getCounterpartIcon(counterpart)} data-flow-v2__card-icon`}
                        aria-hidden="true"
                    />
                    <div>
                        <div className="data-flow-v2__card-name">
                            {counterpart.name || "—"}
                        </div>
                        <div className="data-flow-v2__card-type">
                            {counterpartTypeLabel}
                        </div>
                    </div>
                </div>

                <div className="data-flow-v2__card-actions">
                    {relationLabel && (
                        <span className="data-flow-v2__relation-badge">
                            {relationLabel}
                        </span>
                    )}
                    <Button
                        color="secondary"
                        outline
                        size="sm"
                        type="button"
                        onClick={() => onEdit?.(card)}
                        disabled={isDeleting}
                    >
                        <i className="ri-pencil-line me-1" aria-hidden="true" />
                        {t("Edit")}
                    </Button>
                    <Button
                        color="danger"
                        outline
                        size="sm"
                        type="button"
                        onClick={() => onDelete?.(card)}
                        disabled={isDeleting}
                    >
                        <i
                            className="ri-delete-bin-line me-1"
                            aria-hidden="true"
                        />
                        {t("Delete")}
                    </Button>
                </div>
            </div>

            <div className="data-flow-v2__card-main">
                {visibleMethods.length > 0 && (
                    <div className="data-flow-v2__chip-row">
                        {visibleMethods.map((method) => (
                            <Chip key={method}>
                                {t(methodLabels[method] || method)}
                            </Chip>
                        ))}
                        {methodOverflow > 0 && (
                            <Chip>{`+${methodOverflow}`}</Chip>
                        )}
                    </div>
                )}

                {card.purpose && (
                    <div className="data-flow-v2__card-purpose">
                        {card.purpose}
                    </div>
                )}

                {!isExpanded && (
                    <>
                        <OverflowText
                            items={card.dataTypes}
                            labels={typeLabels}
                            t={t}
                        />
                        <OverflowText
                            items={card.dataCategories}
                            labels={categoryLabels}
                            t={t}
                        />
                    </>
                )}

                {hasDetails && (
                    <button
                        type="button"
                        className="data-flow-v2__details-toggle btn btn-link p-0"
                        onClick={() => setIsExpanded((open) => !open)}
                    >
                        <i
                            className={`me-1 ${
                                isExpanded
                                    ? "ri-arrow-up-s-line"
                                    : "ri-arrow-down-s-line"
                            }`}
                            aria-hidden="true"
                        />
                        {isExpanded ? t("Less") : t("More details")}
                    </button>
                )}
            </div>

            {isExpanded && (
                <div className="data-flow-v2__card-details">
                    <DetailBlock label={t("Purpose")}>
                        {card.purpose || null}
                    </DetailBlock>

                    {card.dataTypes.length > 0 && (
                        <DetailBlock label={t("Data Types")}>
                            {card.dataTypes
                                .map((item) => t(typeLabels[item] || item))
                                .join(", ")}
                        </DetailBlock>
                    )}

                    {card.dataCategories.length > 0 && (
                        <DetailBlock label={t("Data Categories")}>
                            {card.dataCategories
                                .map((item) => t(categoryLabels[item] || item))
                                .join(", ")}
                        </DetailBlock>
                    )}

                    <DetailBlock label={t(identifierLabel)}>
                        {identifier || null}
                    </DetailBlock>
                </div>
            )}
        </div>
    );
};

export default withNamespaces()(DataFlowCard);
